import { Button } from "@heroui/button";
import { Input } from "@heroui/input";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
} from "@heroui/dropdown";
import { ChangeEvent, useCallback } from "react";
import { ChevronDown, Download, Info, Search } from "lucide-react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { AxiosError } from "axios";

import { capitalize } from "@/lib/utils";
import { statuses } from "@/utils/items";
import { useExportData } from "@/features/Admin/Admin.api.ts";
import useTour from "@/hooks/useTour.tsx";

interface TopContentProps {
  columns: any;
  dataLength: number;
  filterValue: string;
  onSearchChange: (value: string) => void;
  onClear: () => void;
  statusFilter: any;
  setStatusFilter: (value: any) => void;
  visibleColumns: any;
  setPage: (value: any) => void;
  setRowsPerPage: (value: any) => void;
  setVisibleColumns: (value: any) => void;
}

const TopContent = ({
  columns,
  dataLength,
  filterValue,
  onSearchChange,
  onClear,
  statusFilter,
  setStatusFilter,
  visibleColumns,
  setVisibleColumns,
  setRowsPerPage,
  setPage,
}: TopContentProps) => {
  const onRowsPerPageChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      setRowsPerPage(Number(e.target.value));
      setPage(1);
    },
    [setPage, setRowsPerPage],
  );
  const { startTour } = useTour();

  const location = useLocation();
  const { mutateAsync, isPending } = useExportData();

  const handleExport = async (format: "csv" | "tsv" | "xlsx") => {
    try {
      await mutateAsync(format);
    } catch (error) {
      if (error instanceof AxiosError) {
        // Handle Axios errors
        toast.error(error.response?.data.message || error.message);
      } else if (error instanceof Error) {
        // Handle generic errors
        toast.error(error.message);
      } else {
        // Handle unknown errors
        toast.error("An unknown error occurred");
      }
    }
  };

  return (
    <div className="w-full overflow-hidden flex flex-col gap-4">
      <div className="flex justify-between gap-3 items-end">
        <Input
          isClearable
          classNames={{
            inputWrapper: "border-[#008c8fff]",
          }}
          className="w-full sm:max-w-[44%]"
          placeholder="Search by name..."
          size="sm"
          variant="underlined"
          startContent={
            <Search
              className="text-neutral-600 dark:text-slate-200"
              size="15"
            />
          }
          value={filterValue}
          onClear={() => onClear()}
          onValueChange={onSearchChange}
        />
        <div className="flex gap-3">
          <Button
            className={"text-[#008c8fff]"}
            endContent={<Info size={16} />}
            href="/admin"
            size="sm"
            variant="light"
            onPress={startTour}
          />
          <Dropdown shouldBlockScroll>
            <DropdownTrigger className="hidden sm:flex">
              <Button
                className={"text-[#008c8fff]"}
                endContent={
                  <ChevronDown
                    className="text-[#008c8fff] dark:text-[#008c8fff]"
                    size="15"
                  />
                }
                size="sm"
                variant="flat"
              >
                Status
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              disallowEmptySelection
              aria-label="Table Columns"
              closeOnSelect={false}
              selectedKeys={statusFilter}
              selectionMode="multiple"
              onSelectionChange={setStatusFilter}
            >
              {statuses.map((status) => (
                <DropdownItem key={status.uid} className="capitalize">
                  {capitalize(status.label)}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <Dropdown>
            <DropdownTrigger className="hidden sm:flex">
              <Button
                className={"text-[#008c8fff]"}
                endContent={
                  <ChevronDown
                    className="text-[#008c8fff] dark:text-[#008c8fff]"
                    size="15"
                  />
                }
                size="sm"
                variant="flat"
              >
                Columns
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              disallowEmptySelection
              aria-label="Table Columns"
              closeOnSelect={false}
              selectedKeys={visibleColumns}
              selectionMode="multiple"
              onSelectionChange={setVisibleColumns}
            >
              {columns.map((column: any) => (
                <DropdownItem key={column.uid} className="capitalize">
                  {capitalize(column.name)}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <div className="flex flex-row gap-2 pr-2">
            <>
              {location.pathname === "/admin" && (
                <>
                  <Dropdown showArrow backdrop="blur">
                    <DropdownTrigger>
                      <Button
                        color={"success"}
                        endContent={
                          <Download
                            className="text-neutral-600 dark:text-slate-200"
                            size="15"
                          />
                        }
                        isLoading={isPending}
                        size="sm"
                        variant="flat"
                      >
                        Export
                      </Button>
                    </DropdownTrigger>
                    <DropdownMenu
                      aria-label="Dropdown menu with description"
                      variant="faded"
                    >
                      <DropdownSection showDivider title={"Export Types"}>
                        <DropdownItem
                          // shortcut="⌘E"
                          key={"csv"}
                          className={"cursor-pointer"}
                          color={"success"}
                          description={
                            <span className={" text-gray-400"}>
                              export all the data to csv
                            </span>
                          }
                          onClick={() => handleExport("csv")}
                        >
                          <span>Export Animals Data CSV format</span>
                        </DropdownItem>
                        <DropdownItem
                          // shortcut="⌘E"
                          key={"excel"}
                          className={"cursor-pointer"}
                          color={"success"}
                          description={
                            <span className={"text-gray-400"}>
                              export all the data to excel
                            </span>
                          }
                          onClick={() => handleExport("xlsx")}
                        >
                          <span>Export Animals Data Excel format</span>
                        </DropdownItem>
                        <DropdownItem
                          // shortcut="⌘E"
                          key={"tsv"}
                          className={"cursor-pointer"}
                          color={"success"}
                          description={
                            <span className={"text-gray-400"}>
                              export all the data to TSV
                            </span>
                          }
                          onPress={() => handleExport("tsv")}
                        >
                          <span>Export Animals Data To TSV Format</span>
                        </DropdownItem>
                      </DropdownSection>
                    </DropdownMenu>
                  </Dropdown>
                </>
              )}
            </>
          </div>
        </div>
      </div>
      <div className="flex justify-between items-center pr-10">
        <span className="text-gray-500 text-small">
          Total {dataLength} data
        </span>
        <label className="flex items-center text-gray-500 text-small">
          Rows per page:
          <select
            className="bg-transparent outline-none text-default-400 text-small"
            onChange={onRowsPerPageChange}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="15">15</option>
          </select>
        </label>
      </div>
    </div>
  );
};

export default TopContent;
